import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash, Plus, Key, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const ROLES = ["hub_user", "manager", "finance", "admin"];
const NEW_USER = { name: "", email: "", password: "", role: "hub_user", hub: "" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(NEW_USER);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [hubFilter, setHubFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/users");
    setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    api.get("/hubs").then(({ data }) => setHubs(data));
  }, [load]);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeHub = async (id, hub) => {
    try {
      await api.patch(`/users/${id}`, { hub: hub === "__none__" ? "" : hub });
      toast.success("Hub updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/auth/register", {
        name: form.name, email: form.email, password: form.password,
        role: form.role, hub: form.hub === "__none__" ? "" : form.hub,
      });
      toast.success("User created");
      setForm(NEW_USER);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setCreating(false);
    }
  };

  const resetPassword = async (id, name) => {
    const pw = window.prompt(`Enter a new password for ${name} (min 6 characters):`);
    if (pw == null) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.patch(`/users/${id}/password`, { password: pw });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900">Users & roles</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Manage who can submit, approve, or administer the platform.
          Only Admin can create accounts and reset passwords.
        </p>
      </div>

      <form onSubmit={createUser} className="mt-6 bg-white border border-zinc-200 rounded-lg p-5" data-testid="create-user-form">
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-3">Create new user</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label htmlFor="nu-name">Name</Label>
            <Input id="nu-name" data-testid="new-user-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="nu-email">Email</Label>
            <Input id="nu-email" data-testid="new-user-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 font-mono" />
          </div>
          <div>
            <Label htmlFor="nu-password">Password</Label>
            <Input id="nu-password" data-testid="new-user-password" type="text" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 font-mono" placeholder="min 6 chars" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="new-user-role" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.filter((r) => r !== "admin").map((r) => (
                  <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Hub</Label>
            <Select value={form.hub || "__none__"} onValueChange={(v) => setForm({ ...form, hub: v })}>
              <SelectTrigger data-testid="new-user-hub" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No hub</SelectItem>
                {hubs.map((h) => (<SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" disabled={creating} data-testid="create-user-btn"
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={16} className="mr-1.5" /> {creating ? "Creating…" : "Create user"}
        </Button>
      </form>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or hub…"
            className="pl-9"
          />
        </div>
        <Select value={hubFilter} onValueChange={setHubFilter}>
          <SelectTrigger data-testid="user-hub-filter" className="w-[170px]">
            <SelectValue placeholder="Hub" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All hubs</SelectItem>
            <SelectItem value="__none__">No hub</SelectItem>
            {hubs.map((h) => (<SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Email</th>
              <th className="px-4 py-2.5 font-medium">Hub</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((u) => {
                const s = search.trim().toLowerCase();
                const matchQ = !s || (u.name || "").toLowerCase().includes(s) ||
                  (u.email || "").toLowerCase().includes(s) || (u.hub || "").toLowerCase().includes(s);
                const matchHub = hubFilter === "all" ||
                  (hubFilter === "__none__" ? !u.hub : u.hub === hubFilter);
                return matchQ && matchHub;
              })
              .map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3 text-zinc-900">{u.name}</td>
                <td className="px-4 py-3 text-zinc-700 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.hub || "__none__"} onValueChange={(v) => changeHub(u.id, v)}>
                    <SelectTrigger data-testid={`hub-select-${u.id}`} className="w-[150px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No hub</SelectItem>
                      {hubs.map((h) => (
                        <SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>
                      ))}
                      {u.hub && !hubs.some((h) => h.name === u.hub) && (
                        <SelectItem value={u.hub}>{u.hub}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                    <SelectTrigger data-testid={`role-select-${u.id}`} className="w-[150px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`reset-password-${u.id}`}
                      onClick={() => resetPassword(u.id, u.name)}
                      title="Reset password"
                      className="text-zinc-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Key size={14} />
                    </Button>
                    {u.id !== me?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => remove(u.id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-4 text-xs text-zinc-500 font-mono">Loading users…</div>}
      </div>
    </DashboardLayout>
  );
}
